import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { BlogPostSummary } from "@tih/shared";
import { fetchBlogPosts } from "../api/client";
import { useSiteCopy } from "../context/SiteCopyContext";
import { CopyText } from "../components/CopyText";
import { getAtPath } from "../lib/siteCopyPaths";

export function BlogIndexPage() {
  const { copy } = useSiteCopy();
  const [posts, setPosts] = useState<BlogPostSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBlogPosts()
      .then(setPosts)
      .catch((e) => setError(String(e?.message ?? e)));
  }, []);

  const emptyCopy =
    String(getAtPath(copy, "blog.empty") ?? "").trim() ||
    "No posts yet. Notes from the room will show up here when they are published.";

  return (
    <section className="section blog-index">
      <div className="sec-eyebrow">
        <em>
          <CopyText path="blog.eyebrow" as="span" />
        </em>
      </div>
      <h1 className="sec-title">
        <CopyText path="blog.title" as="span" />
      </h1>
      <p className="sec-lede" style={{ maxWidth: 720, marginBottom: 56 }}>
        <CopyText path="blog.lede" as="span" />
      </p>

      {error && (
        <div className="page-empty page-empty--error" role="alert">
          {error}
        </div>
      )}

      {!posts ? (
        <div className="page-empty" aria-busy="true">
          Loading…
        </div>
      ) : posts.length === 0 ? (
        <div className="page-empty">
          <p className="page-empty-title">Nothing published yet</p>
          <p>{emptyCopy}</p>
        </div>
      ) : (
        <ul className="blog-list">
          {posts.map((p) => (
            <li key={p.id} className="blog-list-item">
              <Link to={`/blog/${p.slug}`} className="blog-list-link">
                <div className="blog-list-thumb" aria-hidden="true">
                  {p.coverUrl ? (
                    <img src={p.coverUrl} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <div className="photo-ph" data-label={p.title} />
                  )}
                </div>
                <div className="blog-list-content">
                  <div className="blog-list-meta">
                    {p.publishedAt && (
                      <time>
                        {new Date(p.publishedAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </time>
                    )}
                    <span>·</span>
                    <span>{p.authorName}</span>
                  </div>
                  <h2 className="blog-list-title">{p.title}</h2>
                  {p.excerpt && <p className="blog-list-excerpt">{p.excerpt}</p>}
                  <span className="blog-list-cta">
                    Read <span className="arr">→</span>
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
